"use client";

import UserAvatar from "@/components/Avatar/UserAvatar";
import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { Link, useRouter } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { InviteMemberModal } from "@/components/Teams/InviteMemberModal";
import { AssignCourseModal } from "@/components/Teams/AssignCourseModal";
import { TeamSwitcher } from "@/components/Teams/TeamSwitcher";
import { ManagerOnly } from "@/components/Teams/ManagerOnly";
import { SubscriptionExpiredBanner } from "@/components/Teams/SubscriptionExpiredBanner";
import XpDashboard from "@/components/Charts/XpDashboard/XpDashboard";
import { getTeamDetails, getTeamAnalytics, getTeamAssignments } from "@/helpers/api/teams";
import { hasPermission, TeamPermissions } from "@/helpers/teams";
import { useTranslations } from "next-intl";
import moment from "moment-timezone";
import "moment/locale/fr";

export default function TeamsDashboardClient() {
    const params = useParams();
    const locale = params.locale;
    const { data: session, status } = useSession();
    const t = useTranslations("teamsActivity");
    const tDashboard = useTranslations("teamsDashboard");
    const router = useRouter();
    const [teamData, setTeamData] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [dateRange, setDateRange] = useState("week");
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignments, setAssignments] = useState([]);

    const currentTeam = session?.user?.currentTeam;
    const canViewAnalytics = hasPermission(
        session,
        TeamPermissions.VIEW_ANALYTICS,
    );

    // Helper pour obtenir le label de la période
    const getPeriodLabel = () => {
        switch (dateRange) {
            case "today":
                return tDashboard("periodToday");
            case "week":
                return tDashboard("periodWeek");
            case "month":
                return tDashboard("periodMonth");
            case "year":
                return tDashboard("periodYear");
            default:
                return tDashboard("periodWeek");
        }
    };

    // Helper pour calculer la date de début selon la période
    const getStartDate = () => {
        // Utiliser la timezone de l'utilisateur
        const userTimezone = session?.user?.timezone || "Europe/Paris";

        // Obtenir la date actuelle dans la timezone de l'utilisateur
        const nowInUserTz = new Date().toLocaleString("en-US", {
            timeZone: userTimezone,
        });
        const now = new Date(nowInUserTz);

        let startDate;

        switch (dateRange) {
            case "today":
                startDate = new Date(now);
                break;
            case "week":
                // Début de la semaine (lundi) dans la timezone de l'utilisateur
                startDate = new Date(now);
                const day = startDate.getDay();
                const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
                startDate.setDate(diff);
                break;
            case "month":
                // Début du mois (1er jour du mois en cours) dans la timezone de l'utilisateur
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case "year":
                // 12 mois en arrière depuis aujourd'hui dans la timezone de l'utilisateur
                startDate = new Date(now);
                startDate.setFullYear(startDate.getFullYear() - 1);
                break;
            default:
                // Par défaut : début de la semaine
                startDate = new Date(now);
                const defaultDay = startDate.getDay();
                const defaultDiff =
                    startDate.getDate() -
                    defaultDay +
                    (defaultDay === 0 ? -6 : 1);
                startDate.setDate(defaultDiff);
                break;
        }

        // Formater en YYYY-MM-DD
        return startDate.toISOString().split("T")[0];
    };

    // Vérification d'accès
    useEffect(() => {
        if (status === "loading") return;
        if (status === "unauthenticated") {
            router.push("/login");
            return;
        }
        if (!currentTeam) {
            router.push("/teams");
            return;
        }
    }, [session, status, currentTeam, router, locale]);

    // Charger les données réelles
    useEffect(() => {
        if (!currentTeam || !session?.user?.userId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Récupérer les détails de la team
                const timezone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone;

                const teamDetails = await getTeamDetails(
                    currentTeam.id,
                    session.user.userId,
                );
                setTeamData(teamDetails.team || teamDetails);

                // Récupérer les analytics seulement si l'utilisateur a la permission
                if (canViewAnalytics) {
                    try {
                        const teamAnalytics = await getTeamAnalytics(
                            currentTeam.id,
                            session.user.userId,
                            timezone,
                            dateRange,
                        );
                        setAnalytics(teamAnalytics.analytics || teamAnalytics);
                    } catch (error) {
                        console.error(
                            "Erreur lors du chargement des analytics:",
                            error,
                        );
                        // Ne pas bloquer le dashboard si les analytics échouent
                        setAnalytics(null);
                    }
                } else {
                    // Pas de permission analytics, utiliser des données par défaut
                    setAnalytics({
                        stats: {},
                        topCourses: [],
                        recentActivity: [],
                        topPerformers: [],
                        inactiveMembers: [],
                    });
                }

                // Récupérer les assignations
                try {
                    const assignmentsData = await getTeamAssignments(currentTeam.id);
                    setAssignments(assignmentsData.assignments || []);
                } catch (error) {
                    console.error("Erreur lors du chargement des assignations:", error);
                    setAssignments([]);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des données:", error);
                toast.error("Erreur lors du chargement des données");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentTeam?.id, dateRange]);

    const StatCard = ({
        icon,
        title,
        value,
        subtitle,
        trend,
        color = "blue-600",
    }) => (
        <div className="group relative bg-white dark:bg-grayDark-800 hover:bg-gradient-to-br hover:from-white hover:to-gray-50 dark:hover:from-grayDark-800 dark:hover:to-grayDark-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 hover:border-gray-200/50 dark:hover:border-grayDark-600/50 shadow-sm hover:shadow-xl dark:shadow-grayDark-900/20 transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div
                    className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}
                    style={{
                        background:
                            color === "blue-600"
                                ? "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)"
                                : color === "purple-600"
                                ? "linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)"
                                : color === "green-600"
                                ? "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)"
                                : "linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)",
                    }}
                >
                    <SolarIcon
                        name={icon}
                        className={`w-4 h-4 sm:w-5 sm:h-5`}
                        style={{
                            color:
                                color === "blue-600"
                                    ? "#2563EB"
                                    : color === "purple-600"
                                    ? "#9333EA"
                                    : color === "green-600"
                                    ? "#059669"
                                    : "#EA580C",
                        }}
                    />
                </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-grayDark-400 font-medium mb-1 sm:mb-1.5 line-clamp-1">
                {title}
            </p>
            <p className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-grayDark-100 dark:to-grayDark-300 bg-clip-text text-transparent mb-1 tracking-tight">
                {value}
            </p>
            {subtitle && (
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-500 mt-1 mb-0 line-clamp-1">
                    {subtitle}
                </p>
            )}
            {trend && (
                <div className="mt-2 sm:mt-3 flex items-center gap-1 sm:gap-1.5 bg-green-50 dark:bg-green-900/30 rounded-full px-2 sm:px-2.5 py-0.5 sm:py-1 w-fit">
                    <SolarIcon name="graph-up-linear" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600 dark:text-green-400" />
                    <span className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 font-semibold">
                        {trend}
                    </span>
                </div>
            )}
        </div>
    );

    // Loading state
    if (loading || !teamData) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <SolarIcon name="refresh-linear" className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 text-sm">
                        {tDashboard("loadingDashboard")}
                    </p>
                </div>
            </div>
        );
    }

    // Vérifier si l'abonnement est actif (après chargement des données)
    const isSubscriptionActive = teamData?.status === "active" || teamData?.status === "trialing";

    // Fake data pour afficher derrière le blur quand abonnement inactif
    const fakeAnalytics = {
        stats: {
            activeMembers: 8,
            totalXP: 24580,
            totalBadges: 12,
            totalCoursesCompleted: 15,
        },
        topCourses: [
            {
                courseId: 1,
                courseName: "Formation JavaScript",
                enrolledCount: 5,
            },
            { courseId: 2, courseName: "Formation React", enrolledCount: 4 },
            { courseId: 3, courseName: "Formation Node.js", enrolledCount: 3 },
        ],
        recentActivity: [
            {
                userName: "Marie D.",
                courseName: "Formation JavaScript",
                action: "completed",
                xpEarned: 150,
                timestamp: new Date(),
            },
            {
                userName: "Thomas L.",
                courseName: "Formation React",
                action: "progress",
                xpEarned: 50,
                timestamp: new Date(Date.now() - 3600000),
            },
            {
                userName: "Sophie M.",
                courseName: "Formation Node.js",
                action: "session_completed",
                xpEarned: 75,
                timestamp: new Date(Date.now() - 7200000),
            },
        ],
        topPerformers: [
            {
                user: {
                    id: 1,
                    firstName: "Marie",
                    lastName: "Dupont",
                    photo: null,
                },
                xp: 4520,
                badges: 3,
            },
            {
                user: {
                    id: 2,
                    firstName: "Thomas",
                    lastName: "Laurent",
                    photo: null,
                },
                xp: 3890,
                badges: 2,
            },
            {
                user: {
                    id: 3,
                    firstName: "Sophie",
                    lastName: "Martin",
                    photo: null,
                },
                xp: 2750,
                badges: 2,
            },
        ],
        inactiveMembers: [],
    };

    // Utiliser les fake data si abonnement inactif
    const displayAnalytics = !isSubscriptionActive ? fakeAnalytics : analytics;

    return (
        <ManagerOnly locale={locale}>
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-grayDark-900 dark:to-grayDark-800">
                {/* Header */}
                <header className="bg-white/70 dark:bg-grayDark-900/70 backdrop-blur-xl border-b border-gray-200/50 dark:border-grayDark-700/50 sticky top-0 z-40">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Version mobile : 2 lignes */}
                        <div className="sm:hidden py-3 space-y-3">
                            {/* Ligne 1 : Team name + settings */}
                            <div className="flex items-center justify-between">
                                <TeamSwitcher
                                    currentTeam={currentTeam}
                                    availableTeams={
                                        session?.user?.availableTeams || []
                                    }
                                    size="compact"
                                />
                                <Link
                                    href="/teams/dashboard/settings"
                                    className="p-2 hover:bg-gray-100/80 dark:hover:bg-grayDark-700/50 rounded-xl transition-all"
                                >
                                    <SolarIcon name="settings-linear" className="w-5 h-5 text-gray-600 dark:text-grayDark-400" />
                                </Link>
                            </div>
                            {/* Ligne 2 : Actions */}
                            <div className="flex items-center gap-2">
                                <select
                                    value={dateRange}
                                    onChange={(e) =>
                                        setDateRange(e.target.value)
                                    }
                                    className="flex-1 border border-gray-200/80 dark:border-grayDark-600/50 rounded-xl px-3 py-2 text-sm bg-white/50 dark:bg-grayDark-800/50 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-grayDark-700/50 transition-all focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 font-medium text-gray-900 dark:text-grayDark-100"
                                >
                                    <option value="today">
                                        {tDashboard("periodToday")}
                                    </option>
                                    <option value="week">
                                        {tDashboard("periodWeek")}
                                    </option>
                                    <option value="month">
                                        {tDashboard("periodMonth")}
                                    </option>
                                    <option value="year">
                                        {tDashboard("periodYear")}
                                    </option>
                                </select>

                                <button
                                    onClick={() => setShowInviteModal(true)}
                                    disabled={!isSubscriptionActive}
                                    title={
                                        !isSubscriptionActive
                                            ? tDashboard(
                                                  "actionDisabledExpired",
                                              )
                                            : ""
                                    }
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm font-semibold ${
                                        isSubscriptionActive
                                            ? "bg-blue-500 btn-3d lip-blue text-white hover:shadow-lg hover:shadow-blue-500/25"
                                            : "bg-gray-200 dark:bg-grayDark-700 text-gray-400 dark:text-grayDark-500 cursor-not-allowed"
                                    }`}
                                >
                                    <SolarIcon name="user-plus-linear" className="w-4 h-4" />
                                    {tDashboard("invite")}
                                </button>
                            </div>
                        </div>

                        {/* Version desktop : 1 ligne */}
                        <div className="hidden sm:flex items-center justify-between h-16 gap-4">
                            <div className="flex items-center gap-4 min-w-0 flex-shrink">
                                <TeamSwitcher
                                    currentTeam={currentTeam}
                                    availableTeams={
                                        session?.user?.availableTeams || []
                                    }
                                />
                            </div>

                            <div className="flex items-center gap-3 flex-shrink-0">
                                <select
                                    value={dateRange}
                                    onChange={(e) =>
                                        setDateRange(e.target.value)
                                    }
                                    className="border border-gray-200/80 dark:border-grayDark-600/50 rounded-2xl px-4 py-2.5 text-sm bg-white/50 dark:bg-grayDark-800/50 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-grayDark-700/50 transition-all focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 font-medium text-gray-900 dark:text-grayDark-100"
                                >
                                    <option value="today">
                                        {tDashboard("periodToday")}
                                    </option>
                                    <option value="week">
                                        {tDashboard("periodWeek")}
                                    </option>
                                    <option value="month">
                                        {tDashboard("periodMonth")}
                                    </option>
                                    <option value="year">
                                        {tDashboard("periodYear")}
                                    </option>
                                </select>

                                <button
                                    onClick={() => setShowInviteModal(true)}
                                    disabled={!isSubscriptionActive}
                                    title={
                                        !isSubscriptionActive
                                            ? tDashboard(
                                                  "actionDisabledExpired",
                                              )
                                            : ""
                                    }
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl transition-all text-sm font-semibold ${
                                        isSubscriptionActive
                                            ? "bg-blue-500 btn-3d lip-blue text-white hover:shadow-lg hover:shadow-blue-500/25 dark:hover:shadow-blue-400/25 hover:-translate-y-0.5"
                                            : "bg-gray-200 dark:bg-grayDark-700 text-gray-400 dark:text-grayDark-500 cursor-not-allowed"
                                    }`}
                                >
                                    <SolarIcon name="user-plus-linear" className="w-4 h-4" />
                                    {tDashboard("invite")}
                                </button>

                                <Link
                                    href="/teams/dashboard/settings"
                                    className="p-2.5 hover:bg-gray-100/80 dark:hover:bg-grayDark-700/50 rounded-2xl transition-all hover:scale-105"
                                >
                                    <SolarIcon name="settings-linear" className="w-5 h-5 text-gray-600 dark:text-grayDark-400" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Quick Stats */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
                    {/* Subscription Expired/Past Due Banner */}
                    {!isSubscriptionActive && <SubscriptionExpiredBanner status={teamData?.status} />}
                    <div
                        className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8 ${
                            !isSubscriptionActive
                                ? "blur-sm pointer-events-none select-none"
                                : ""
                        }`}
                    >
                        <StatCard
                            icon="users-group-rounded-linear"
                            title={tDashboard("activeCollaborators")}
                            value={`${
                                displayAnalytics.stats?.activeMembers || 0
                            }/${teamData.totalSeats || 0}`}
                            subtitle={getPeriodLabel()}
                            color="blue-600"
                        />
                        <StatCard
                            icon="medal-ribbon-star-linear"
                            title={tDashboard("totalTeamXp")}
                            value={(
                                displayAnalytics.stats?.totalXP || 0
                            ).toLocaleString(locale)}
                            subtitle={tDashboard("badgesEarned", {
                                count: displayAnalytics.stats?.totalBadges || 0,
                            })}
                            color="purple-600"
                        />
                        <StatCard
                            icon="book-2-linear"
                            title={tDashboard("coursesCompleted")}
                            value={
                                displayAnalytics.stats?.totalCoursesCompleted ||
                                0
                            }
                            subtitle={getPeriodLabel()}
                            color="green-600"
                        />
                        <StatCard
                            icon="pulse-linear"
                            title={tDashboard("availableSeats")}
                            value={
                                (teamData.totalSeats || 0) -
                                (teamData.occupiedSeats || 0)
                            }
                            subtitle={tDashboard("outOfTotal", {
                                total: teamData.totalSeats || 0,
                            })}
                            color="orange-600"
                        />
                    </div>

                    {/* Graphique de progression XP de l'équipe */}
                    {teamData?.members && teamData.members.length > 0 && (
                        <div className="max-w-7xl mx-auto pb-4 sm:pb-5">
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-xl dark:shadow-grayDark-900/20 transition-shadow relative">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                                    <h2 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent">
                                        {tDashboard("teamProgress")}
                                    </h2>
                                    <span className="text-xs sm:text-sm bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold w-fit">
                                        {getPeriodLabel()}
                                    </span>
                                </div>
                                <div
                                    className={
                                        !isSubscriptionActive
                                            ? "blur-sm pointer-events-none select-none"
                                            : ""
                                    }
                                >
                                    <XpDashboard
                                        users={teamData.members.map(
                                            (m) => m.userId,
                                        )}
                                        userTimezone={session?.user?.timezone}
                                        showAllTime={
                                            dateRange === "month" ||
                                            dateRange === "year"
                                        }
                                        startDate={getStartDate()}
                                        displayMode={
                                            dateRange === "today"
                                                ? "daily"
                                                : "auto"
                                        }
                                        showStats={true}
                                    />
                                </div>
                                {!isSubscriptionActive && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-white/90 dark:bg-grayDark-800/90 rounded-2xl px-6 py-4 shadow-lg text-center">
                                            <p className="text-gray-700 dark:text-grayDark-300 font-medium mb-0">
                                                {tDashboard(
                                                    "subscriptionExpiredBlur",
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div
                        className={`grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 ${
                            !isSubscriptionActive
                                ? "blur-sm pointer-events-none select-none"
                                : ""
                        }`}
                    >
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                            {/* Top Courses */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent">
                                        {tDashboard("popularCourses")}
                                    </h2>
                                    {canViewAnalytics && (
                                        <Link
                                            href="/teams/dashboard/analytics"
                                            className="text-blue-600 dark:text-blue-400 text-xs sm:text-sm font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1 group"
                                        >
                                            {tDashboard("viewAll")}
                                            <span className="group-hover:translate-x-0.5 transition-transform">
                                                →
                                            </span>
                                        </Link>
                                    )}
                                </div>

                                <div className="space-y-2 sm:space-y-3">
                                    {(() => {
                                        const courses = (
                                            displayAnalytics.topCourses || []
                                        ).slice(0, 3);
                                        const totalEnrolled = courses.reduce(
                                            (sum, c) =>
                                                sum + (c.enrolledCount || 0),
                                            0,
                                        );
                                        return courses.map((course) => {
                                            const percentage =
                                                totalEnrolled > 0
                                                    ? Math.round(
                                                          (course.enrolledCount /
                                                              totalEnrolled) *
                                                              100,
                                                      )
                                                    : 0;
                                            return (
                                                <div
                                                    key={course.courseId}
                                                    className="group flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-50/50 dark:from-grayDark-700/50 dark:to-grayDark-700/30 rounded-xl sm:rounded-2xl hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 transition-all hover:scale-[1.02] cursor-pointer"
                                                >
                                                    <div className="flex-1 min-w-0 mr-3">
                                                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-grayDark-100 mb-0.5 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors truncate">
                                                            {course.courseName}
                                                        </h3>
                                                        <p className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                                            <span className="font-medium">
                                                                {
                                                                    course.enrolledCount
                                                                }
                                                            </span>{" "}
                                                            {tDashboard(
                                                                "enrolled",
                                                                {
                                                                    count: course.enrolledCount,
                                                                },
                                                            )}
                                                        </p>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-grayDark-100">
                                                            {percentage}%
                                                        </div>
                                                        <div className="w-16 sm:w-24 h-1.5 sm:h-2 bg-gray-200/80 dark:bg-grayDark-600/50 rounded-full mt-1 sm:mt-1.5 overflow-hidden">
                                                            <div
                                                                className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-500 ease-out"
                                                                style={{
                                                                    width: `${percentage}%`,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                <button
                                    onClick={() => setShowAssignModal(true)}
                                    disabled={!isSubscriptionActive}
                                    className={`mt-4 sm:mt-6 flex items-center justify-center gap-2 w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl transition-all text-xs sm:text-sm font-semibold ${
                                        isSubscriptionActive
                                            ? "bg-blue-500 btn-3d lip-blue text-white hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5"
                                            : "bg-gray-200 dark:bg-grayDark-700 text-gray-400 dark:text-grayDark-500 cursor-not-allowed"
                                    }`}
                                >
                                    <SolarIcon name="add-linear" className="w-4 h-4" />
                                    {tDashboard("assignCourse")}
                                </button>
                            </div>

                            {/* Activity Feed */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent mb-4 sm:mb-6">
                                    {t("recentActivity")}
                                </h2>

                                <div className="space-y-1 sm:space-y-2">
                                    {(displayAnalytics.recentActivity || [])
                                        .length > 0 ? (
                                        (displayAnalytics.recentActivity || [])
                                            .slice(0, 5)
                                            .map((activity, index) => {
                                                // Déterminer le texte d'action et le détail selon le type
                                                const getActionDetails = (
                                                    activity,
                                                ) => {
                                                    const action =
                                                        activity.action;
                                                    const activitySession =
                                                        activity.session;
                                                    const isQuiz =
                                                        activitySession?.type ===
                                                            "quiz" ||
                                                        action ===
                                                            "quiz_completed";

                                                    if (
                                                        action === "completed"
                                                    ) {
                                                        return {
                                                            verb: t(
                                                                "actionCompletedCourse",
                                                            ),
                                                            detail: null,
                                                        };
                                                    }
                                                    if (action === "badge") {
                                                        return {
                                                            verb: t(
                                                                "actionBadge",
                                                            ),
                                                            detail: null,
                                                        };
                                                    }
                                                    if (
                                                        action ===
                                                        "quiz_completed"
                                                    ) {
                                                        return {
                                                            verb: t(
                                                                "actionQuizCompleted",
                                                            ),
                                                            detail: activitySession?.title,
                                                            score: activitySession?.score,
                                                        };
                                                    }
                                                    // Progression sur un quiz (réponse à une question)
                                                    if (
                                                        action === "progress" &&
                                                        isQuiz
                                                    ) {
                                                        return {
                                                            verb: t(
                                                                "actionQuizProgress",
                                                            ),
                                                            detail: null,
                                                        };
                                                    }
                                                    if (
                                                        action ===
                                                        "session_completed"
                                                    ) {
                                                        return {
                                                            verb: t(
                                                                "actionSessionCompleted",
                                                            ),
                                                            detail: activitySession?.title,
                                                        };
                                                    }
                                                    // progress par défaut
                                                    return {
                                                        verb: t(
                                                            "actionProgress",
                                                        ),
                                                        detail: activitySession?.title,
                                                    };
                                                };

                                                // Support des deux formats (ancien et nouveau)
                                                const userName =
                                                    activity.userName ||
                                                    (activity.user
                                                        ? `${
                                                              activity.user
                                                                  .firstName ||
                                                              ""
                                                          } ${
                                                              activity.user
                                                                  .lastName ||
                                                              ""
                                                          }`.trim()
                                                        : "Un membre");
                                                const courseName =
                                                    activity.courseName ||
                                                    activity.course?.title ||
                                                    "une formation";
                                                const xpEarned =
                                                    activity.xpEarned ||
                                                    activity.xpGained;
                                                const actionDetails =
                                                    getActionDetails(activity);

                                                return (
                                                    <div
                                                        key={index}
                                                        className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-gray-50 dark:hover:bg-grayDark-700/50 rounded-lg sm:rounded-xl transition-colors"
                                                    >
                                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 dark:bg-grayDark-700 flex items-center justify-center flex-shrink-0">
                                                            {activity.user
                                                                ?.photo ? (
                                                                <UserAvatar plain
                                                                    user={activity.user}
                                                                    alt={
                                                                        userName
                                                                    }
                                                                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover"
                                                                />
                                                            ) : (
                                                                <SolarIcon name="book-2-linear" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-grayDark-400" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs sm:text-sm mb-0">
                                                                <span className="font-medium text-gray-900 dark:text-grayDark-100">
                                                                    {userName}
                                                                </span>{" "}
                                                                <span className="text-gray-600 dark:text-grayDark-400">
                                                                    {
                                                                        actionDetails.verb
                                                                    }
                                                                </span>{" "}
                                                                {actionDetails.detail ? (
                                                                    <>
                                                                        {activity
                                                                            .course
                                                                            ?.slug &&
                                                                        activity
                                                                            .session
                                                                            ?.part
                                                                            ?.number &&
                                                                        activity
                                                                            .session
                                                                            ?.position ? (
                                                                            <Link
                                                                                href={`/courses/${activity.course.slug}/${activity.session.part.number}/${activity.session.position}`}
                                                                                className="font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline"
                                                                            >
                                                                                {
                                                                                    actionDetails.detail
                                                                                }
                                                                            </Link>
                                                                        ) : (
                                                                            <span className="font-medium text-purple-600 dark:text-purple-400">
                                                                                {
                                                                                    actionDetails.detail
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        <span className="text-gray-600 dark:text-grayDark-400">
                                                                            {" "}
                                                                            {t(
                                                                                "in",
                                                                            )}{" "}
                                                                        </span>
                                                                    </>
                                                                ) : null}
                                                                {activity.course
                                                                    ?.slug ? (
                                                                    <Link
                                                                        href={`/courses/${activity.course.slug}`}
                                                                        className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                                                                    >
                                                                        {
                                                                            courseName
                                                                        }
                                                                    </Link>
                                                                ) : (
                                                                    <span className="font-medium text-blue-600 dark:text-blue-400">
                                                                        {
                                                                            courseName
                                                                        }
                                                                    </span>
                                                                )}
                                                                {actionDetails.score !==
                                                                    undefined && (
                                                                    <span className="text-gray-500 dark:text-grayDark-400">
                                                                        {" "}
                                                                        (
                                                                        {
                                                                            actionDetails.score
                                                                        }
                                                                        %)
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mt-0.5 mb-0">
                                                                {moment(
                                                                    activity.timestamp,
                                                                )
                                                                    .locale(
                                                                        locale,
                                                                    )
                                                                    .tz(
                                                                        session
                                                                            ?.user
                                                                            ?.timezone ||
                                                                            "Europe/Paris",
                                                                    )
                                                                    .fromNow()
                                                                    .charAt(0)
                                                                    .toUpperCase() +
                                                                    moment(
                                                                        activity.timestamp,
                                                                    )
                                                                        .locale(
                                                                            locale,
                                                                        )
                                                                        .tz(
                                                                            session
                                                                                ?.user
                                                                                ?.timezone ||
                                                                                "Europe/Paris",
                                                                        )
                                                                        .fromNow()
                                                                        .slice(
                                                                            1,
                                                                        )}
                                                            </p>
                                                        </div>
                                                        {xpEarned > 0 &&
                                                            activity.action !==
                                                                "progress" && (
                                                                <div className="text-[10px] sm:text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg flex-shrink-0">
                                                                    +{xpEarned}{" "}
                                                                    XP
                                                                </div>
                                                            )}
                                                    </div>
                                                );
                                            })
                                    ) : (
                                        <div className="text-center py-6 sm:py-8">
                                            <SolarIcon name="pulse-linear" className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 dark:text-grayDark-600 mx-auto mb-2 sm:mb-3" />
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {t("noActivity")}
                                            </p>
                                            <p className="text-[10px] sm:text-xs text-gray-400 dark:text-grayDark-500 mb-0">
                                                {t("noActivityDescription")}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {(displayAnalytics.recentActivity || [])
                                    .length > 0 && (
                                    <Link
                                        href="/teams/dashboard/activity"
                                        className="mt-3 sm:mt-4 block text-center text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                    >
                                        {t("viewAll")}
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-4 sm:space-y-6">
                            {/* Top Performers */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent">
                                        {tDashboard("topPerformers")}
                                    </h2>
                                    <span className="text-[10px] sm:text-xs bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 text-orange-700 dark:text-orange-400 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-semibold">
                                        {getPeriodLabel()}
                                    </span>
                                </div>

                                <div className="space-y-2 sm:space-y-3">
                                    {(displayAnalytics.topPerformers || [])
                                        .sort(
                                            (a, b) => (b.xp || 0) - (a.xp || 0),
                                        )
                                        .slice(0, 3)
                                        .map((performer, index) => (
                                            <Link
                                                href={`/@${
                                                    performer.user?.pseudo
                                                        ? performer.user.pseudo
                                                        : `believer-${performer.user.id}`
                                                }`}
                                                key={
                                                    "performer-link-" +
                                                    performer.user?.id
                                                }
                                            >
                                                <div className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-lg sm:rounded-xl hover:bg-gray-50 dark:hover:bg-grayDark-700/50 transition-colors">
                                                    <div className="relative">
                                                        {performer.user
                                                            ?.photo ? (
                                                            <UserAvatar plain
                                                                user={performer.user}
                                                                alt={
                                                                    performer
                                                                        .user
                                                                        .firstName
                                                                }
                                                                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 dark:bg-grayDark-700 flex items-center justify-center font-medium text-gray-600 dark:text-grayDark-300 text-xs sm:text-sm">
                                                                {
                                                                    performer
                                                                        .user
                                                                        ?.firstName?.[0]
                                                                }
                                                                {
                                                                    performer
                                                                        .user
                                                                        ?.lastName?.[0]
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-gray-900 dark:text-grayDark-100 text-xs sm:text-sm mb-0 truncate">
                                                            {
                                                                performer.user
                                                                    ?.firstName
                                                            }{" "}
                                                            {
                                                                performer.user
                                                                    ?.lastName
                                                            }
                                                        </p>
                                                        <p className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                                            {(
                                                                performer.xp ||
                                                                0
                                                            ).toLocaleString(
                                                                locale,
                                                            )}{" "}
                                                            XP
                                                            {performer.badges >
                                                                0 &&
                                                                ` · ${
                                                                    performer.badges
                                                                } ${tDashboard(
                                                                    "badge",
                                                                    {
                                                                        count: performer.badges,
                                                                    },
                                                                )}`}
                                                        </p>
                                                    </div>
                                                    <div
                                                        className={`text-xs sm:text-sm font-semibold ${
                                                            index === 0
                                                                ? "text-yellow-600 dark:text-yellow-400"
                                                                : "text-gray-400 dark:text-grayDark-500"
                                                        }`}
                                                    >
                                                        #{index + 1}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                </div>

                                {canViewAnalytics && (
                                    <Link
                                        href="/teams/dashboard/analytics"
                                        className="mt-3 sm:mt-4 block text-center text-xs sm:text-sm text-blue-600 font-medium hover:text-blue-700"
                                    >
                                        {tDashboard("viewFullRanking")}
                                    </Link>
                                )}
                            </div>

                            {/* Inactive Alerts */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 flex items-center justify-center shadow-sm">
                                        <SolarIcon name="danger-circle-linear" className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent">
                                        {tDashboard("inactiveMembers")}
                                    </h2>
                                </div>

                                {displayAnalytics.inactiveMembers &&
                                displayAnalytics.inactiveMembers.length > 0 ? (
                                    <>
                                        <div className="space-y-1.5 sm:space-y-2">
                                            {displayAnalytics.inactiveMembers
                                                .slice(0, 3)
                                                .map((member) => {
                                                    const daysSinceActive =
                                                        member.daysSinceLastActivity ||
                                                        0;
                                                    const isHighRisk =
                                                        daysSinceActive > 7;

                                                    return (
                                                        <div
                                                            key={member.userId}
                                                            className={`p-2.5 sm:p-3 rounded-lg sm:rounded-xl ${
                                                                isHighRisk
                                                                    ? "bg-red-50"
                                                                    : "bg-yellow-50"
                                                            }`}
                                                        >
                                                            <p className="font-medium text-gray-900 text-xs sm:text-sm mb-0">
                                                                {
                                                                    member.firstName
                                                                }{" "}
                                                                {
                                                                    member.lastName
                                                                }
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-gray-600 mb-0">
                                                                {tDashboard(
                                                                    "lastActivity",
                                                                    {
                                                                        days: daysSinceActive,
                                                                    },
                                                                )}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        <Link
                                            href="/teams/dashboard/members"
                                            className="mt-3 sm:mt-4 block w-full text-center text-xs sm:text-sm text-blue-600 font-medium hover:text-blue-700"
                                        >
                                            {tDashboard("viewAllMembers")}
                                        </Link>
                                    </>
                                ) : (
                                    <div className="text-center py-4 sm:py-6">
                                        <div className="text-2xl sm:text-3xl mb-2">🎉</div>
                                        <p className="text-xs sm:text-sm text-gray-600 mb-0">
                                            {tDashboard("allMembersActive")}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Recent Assignments */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center shadow-sm">
                                            <SolarIcon name="clipboard-text-linear" className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent">
                                            {tDashboard("recentAssignments")}
                                        </h2>
                                    </div>
                                    {assignments.length > 0 && (
                                        <span className="text-[10px] sm:text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-semibold">
                                            {assignments.length}
                                        </span>
                                    )}
                                </div>

                                {assignments.length > 0 ? (
                                    <>
                                        <div className="space-y-2 sm:space-y-3">
                                            {assignments.slice(0, 4).map((assignment, index) => (
                                                <div
                                                    key={assignment.id || index}
                                                    className="p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-gray-50 dark:bg-grayDark-700/50 hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-colors"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-gray-900 dark:text-grayDark-100 text-xs sm:text-sm mb-0.5 truncate">
                                                                {assignment.course?.title || assignment.courseTitle || tDashboard("unknownCourse")}
                                                            </p>
                                                            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                                                {assignment.member?.firstName || assignment.memberFirstName}{" "}
                                                                {assignment.member?.lastName || assignment.memberLastName}
                                                            </p>
                                                        </div>
                                                        {(assignment.dueDate || assignment.due_date) && (
                                                            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 shrink-0">
                                                                <SolarIcon name="clock-circle-linear" className="w-3 h-3" />
                                                                <span>
                                                                    {moment(assignment.dueDate || assignment.due_date).format("DD/MM")}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {assignment.progress !== undefined && (
                                                        <div className="mt-2">
                                                            <div className="h-1.5 bg-gray-200 dark:bg-grayDark-600 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-blue-500 rounded-full transition-all"
                                                                    style={{ width: `${assignment.progress || 0}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <Link
                                            href="/teams/dashboard/assignments"
                                            className="mt-3 sm:mt-4 block w-full text-center text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300"
                                        >
                                            {tDashboard("viewAllAssignments")}
                                        </Link>
                                    </>
                                ) : (
                                    <div className="text-center py-4 sm:py-6">
                                        <div className="text-2xl sm:text-3xl mb-2">📚</div>
                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-grayDark-400 mb-2">
                                            {tDashboard("noAssignments")}
                                        </p>
                                        <button
                                            onClick={() => setShowAssignModal(true)}
                                            disabled={!isSubscriptionActive}
                                            className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {tDashboard("assignCourse")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Quick Actions */}
                            <div className="bg-white/80 dark:bg-grayDark-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-100/50 dark:border-grayDark-700/50 shadow-sm hover:shadow-lg dark:shadow-grayDark-900/20 transition-shadow">
                                <h2 className="text-base sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-grayDark-100 dark:to-grayDark-400 bg-clip-text text-transparent mb-4 sm:mb-5">
                                    {tDashboard("quickActions")}
                                </h2>

                                <div className="space-y-1 sm:space-y-1.5">
                                    {canViewAnalytics && (
                                        <>
                                            <Link
                                                href="/teams/dashboard/analytics"
                                                className="flex items-center justify-between p-2.5 sm:p-3 hover:bg-gray-50 dark:hover:bg-grayDark-700/50 rounded-lg sm:rounded-xl transition-colors group"
                                            >
                                                <div className="flex items-center gap-2 sm:gap-3">
                                                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-gray-100 dark:bg-grayDark-700 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-grayDark-600 transition-colors">
                                                        <SolarIcon name="chart-2-linear" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-grayDark-400" />
                                                    </div>
                                                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-grayDark-100">
                                                        {tDashboard(
                                                            "detailedAnalytics",
                                                        )}
                                                    </span>
                                                </div>
                                                <SolarIcon name="alt-arrow-right-linear" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 dark:text-grayDark-500" />
                                            </Link>
                                        </>
                                    )}

                                    <Link
                                        href="/teams/dashboard/members"
                                        className="flex items-center justify-between p-2.5 sm:p-3 hover:bg-gray-50 dark:hover:bg-grayDark-700/50 rounded-lg sm:rounded-xl transition-colors group"
                                    >
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-gray-100 dark:bg-grayDark-700 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-grayDark-600 transition-colors">
                                                <SolarIcon name="users-group-rounded-linear" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-grayDark-400" />
                                            </div>
                                            <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-grayDark-100">
                                                {tDashboard("manageMembers")}
                                            </span>
                                        </div>
                                        <SolarIcon name="alt-arrow-right-linear" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 dark:text-grayDark-500" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invite Modal */}
                <InviteMemberModal
                    isOpen={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    teamId={currentTeam?.id}
                    onSuccess={() => {
                        setShowInviteModal(false);
                        // Refresh data if needed
                    }}
                />

                {/* Assign Course Modal */}
                <AssignCourseModal
                    isOpen={showAssignModal}
                    onClose={() => setShowAssignModal(false)}
                    teamId={currentTeam?.id}
                    members={teamData?.members || []}
                    onSuccess={() => {
                        setShowAssignModal(false);
                    }}
                />
            </div>
        </ManagerOnly>
    );
}
